import { useRef } from "react";
import { useAuth } from "react-oidc-context";
import { datadogRum } from "@datadog/browser-rum";

import FailureErrorImage from "assets/failure.svg";
import { isNotFoundError, isSessionExpiredError } from "common/apiError";
import { useUserByIdQuery } from "core/queries";
import CurrentUserContext from "./CurrentUserContext";
import AnimateProviderState from "core/components/AnimateProviderState";
import Loader from "core/components/Loader";
import ProfileForm from "./ProfileForm";
import { REVERSE_LOOKUP_ROLES, ROLES } from "core/constants";
import { getUserStatus } from "pages/admin/user-profile/constants";
import Flex from "core/components/Flex";
import Typography from "core/components/Typography";
import Button from "core/components/Button";

const MissingProfilePage = () => {
  const auth = useAuth();

  return (
    <Flex
      direction="column"
      alignItems="center"
      height="100dvh"
      style={{ paddingTop: "20dvh" }}
      gap="24px"
    >
      <img
        src={FailureErrorImage}
        alt="failure error page"
        style={{
          display: "block",
          width: "455px",
          margin: "0px auto",
        }}
      />
      <Typography variant="h3" noMargin>
        You do not have a Populi account. Please contact support.
      </Typography>
      <Button variant="contained" onClick={auth.signoutRedirect}>
        Sign Out
      </Button>
    </Flex>
  );
};

// A profile that failed to load is not a profile that doesn't exist. The
// network dropping mid-request — which is what waking a laptop does to every
// in-flight call — has to read as "try again", not as "you have no account".
const ProfileUnavailablePage = ({ onRetry }) => (
  <Flex
    direction="column"
    alignItems="center"
    height="100dvh"
    style={{ paddingTop: "20dvh" }}
    gap="24px"
  >
    <img
      src={FailureErrorImage}
      alt="failure error page"
      style={{
        display: "block",
        width: "455px",
        margin: "0px auto",
      }}
    />
    <Typography variant="h3" noMargin>
      We couldn&apos;t reach Populi. Check your connection and try again.
    </Typography>
    <Button variant="contained" onClick={onRetry}>
      Try Again
    </Button>
  </Flex>
);

const CurrentUserProvider = ({ children }) => {
  const auth = useAuth();
  const userId = auth.user?.profile?.sub;
  const ddInitialized = useRef(false);

  const {
    data: currentUser,
    error,
    isLoading,
    refetch,
  } = useUserByIdQuery({
    userId,
    enabled: !!auth.user?.access_token && !!userId,
    select: (data) => {
      return {
        ...data,
        userId: data.user_id,
        status: getUserStatus({ isActive: data.enabled }),
        alternateEmail: data.alternate_email ?? "",
      };
    },
  });

  // DataDog init during render - user-level fields only
  if (currentUser && !ddInitialized.current) {
    const existingUser = datadogRum.getUser();
    if (!existingUser?.email) {
      datadogRum.setUser({
        name: currentUser.name,
        email: currentUser.email,
      });
    }
    ddInitialized.current = true;
  }

  // Only a 404 means what MissingProfilePage says. A 401 is a dead session,
  // already being redirected to Keycloak by HandleSessionExpiry; anything else
  // is the request itself having failed.
  const isMissingProfile = isNotFoundError(error);
  const isExpiredSession = isSessionExpiredError(error);
  const isUnavailable = !!error && !isMissingProfile && !isExpiredSession;

  return (
    <CurrentUserContext.Provider value={{ currentUser, isLoading }}>
      <AnimateProviderState
        states={[
          {
            key: "loading",
            shouldRender: isLoading,
            child: (
              <Loader
                style={{
                  height: "100dvh",
                  paddingBottom: "20vh",
                }}
              />
            ),
          },
          {
            key: "missing-profile",
            shouldRender: !isLoading && isMissingProfile,
            child: <MissingProfilePage />,
          },
          {
            key: "expired-session",
            shouldRender: !isLoading && isExpiredSession,
            child: (
              <Loader
                style={{
                  height: "100dvh",
                  paddingBottom: "20vh",
                }}
              />
            ),
          },
          {
            key: "profile-unavailable",
            shouldRender: !isLoading && isUnavailable,
            child: <ProfileUnavailablePage onRetry={() => refetch()} />,
          },
          {
            key: "complete-profile",
            shouldRender:
              !isLoading && !error && currentUser && !currentUser.name,
            child: <ProfileForm />,
          },
          {
            key: "main",
            shouldRender: !isLoading && !error && currentUser?.name,
            child: children,
          },
        ]}
      />
    </CurrentUserContext.Provider>
  );
};

export default CurrentUserProvider;
